import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { SettlementCard } from './SettlementCard';
import { dayLabel, SETTLEMENTS_KEY, STATES, type SettlementState, type Summary } from './types';
import './settlements.css';

const money = (v: number | undefined) => (v === undefined ? '—' : formatPln(v));
const warsawToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date());
/** From the first day of the previous month to the last day of this one (Warsaw). */
function defaultPeriod(): { from: string; to: string } {
  const today = warsawToday();
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const prev = m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
  const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return { from: prev, to: last };
}

/**
 * "Finanse → Rozliczenia" (F39): every person × event in the period (by the event's first day) with the state, the
 * total, what was paid and what is due; sums at the bottom. A row opens the settlement's card.
 */
function SettlementsList() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(defaultPeriod);
  const [state, setState] = useState<SettlementState | ''>('');
  const params = new URLSearchParams({ ...(period.from ? { from: period.from } : {}), ...(period.to ? { to: period.to } : {}), ...(state ? { state } : {}) });
  const list = useQuery({ queryKey: [...SETTLEMENTS_KEY, 'list', params.toString()], queryFn: () => api<Summary>(`/api/settlements?${params.toString()}`) });
  const rows = list.data?.rows ?? [];
  const totals = list.data?.totals;
  return (
    <section aria-label="Zestawienie rozliczeń" data-feature-id="settlements.list">
      <p className="lede">Co firma jest winna ludziom za wydarzenia i co już wypłaciła. Rozliczenia powstają dzień po wydarzeniu dla obsady.</p>
      <div className="fin-filters stl-filters">
        <label className="field">
          <span>Od</span>
          <input type="date" value={period.from} onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))} />
        </label>
        <label className="field">
          <span>Do</span>
          <input type="date" value={period.to} onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))} />
        </label>
        <label className="field">
          <span>Stan</span>
          <select value={state} onChange={(e) => setState(e.target.value as SettlementState | '')}>
            <option value="">Wszystkie</option>
            {(Object.keys(STATES) as SettlementState[]).map((s) => (
              <option key={s} value={s}>
                {STATES[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać rozliczeń'}</p>}
      {list.data && rows.length === 0 && <p className="muted">Brak rozliczeń w tym okresie.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table stl-list" aria-label="Rozliczenia ludzi">
            <thead>
              <tr>
                <th scope="col">Wydarzenie</th>
                <th scope="col">Osoba</th>
                <th scope="col">Stan</th>
                <th scope="col" className="num">
                  Razem
                </th>
                <th scope="col" className="num">
                  Wypłacono
                </th>
                <th scope="col" className="num">
                  Do wypłaty
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.settlementId} className="clickable" onClick={() => navigate(`/finanse/rozliczenia/${r.settlementId}`)}>
                  <td>
                    <span className="muted">{dayLabel(r.eventFirstDay)}</span> {r.eventName}
                  </td>
                  <td>
                    <Link to={`/finanse/rozliczenia/${r.settlementId}`} onClick={(e) => e.stopPropagation()}>
                      {r.personName}
                    </Link>
                  </td>
                  <td>{STATES[r.state]}</td>
                  <td className="num">{money(r.totalGrosze)}</td>
                  <td className="num">{money(r.paidGrosze)}</td>
                  <td className="num">{money(r.dueGrosze)}</td>
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={3}>
                    Razem
                  </th>
                  <td className="num">{formatPln(totals.totalGrosze)}</td>
                  <td className="num">{formatPln(totals.paidGrosze)}</td>
                  <td className="num">{formatPln(totals.dueGrosze)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  );
}

/** `/finanse/rozliczenia` (the list) and `/finanse/rozliczenia/:id` (one card). */
export function SettlementsPage() {
  const { me } = useMe();
  const { id } = useParams();
  if (!me) return null;
  return (
    <section className="page wide finance-page">
      {id ? (
        <>
          <p>
            <Link to="/finanse/rozliczenia">← Wszystkie rozliczenia</Link>
          </p>
          <SettlementCard id={id} me={me} />
        </>
      ) : (
        <SettlementsList />
      )}
    </section>
  );
}
